
import React, { useEffect, useState, useRef } from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Swal from "sweetalert2";
import apiInstance, { imageBaseUrl } from '../utils/apiInstance';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css'
import '../common/Table.css';
import { useSelector } from 'react-redux';
import AccessDenied from "../common/AccessDenied";

const CategoryList = () => {
    const userDetails = useSelector((state) => state.users.user);
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalPages, setTotalPages] = useState(1);
    const [limit] = useState(10);
    const [searchParams, setSearchParams] = useSearchParams();
    const initialPage = parseInt(searchParams.get("page")) || 1;
    const initialSearch = searchParams.get("search") || "";
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [searchQuery, setSearchQuery] = useState(initialSearch);

    const fetchData = async (page = 1, search = '') => {
        setLoading(true);
        try {
            const response = await apiInstance.get(`/service_list`, {
                params: {
                    page,
                    limit,
                    search
                }
            });
            setData(response?.data?.service_list);
            setTotalPages(response?.data?.totalPages);
            setCurrentPage(response?.data?.currentPage);
            setSearchParams({ page, search }, { replace: true });
        } catch (error) {
            setError("An error occurred while fetching services...");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const page = parseInt(searchParams.get("page")) || 1;
        const search = searchParams.get("search") || "";
        setSearchQuery(search);
        fetchData(page, search);
    }, [searchParams]);


    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setSearchParams({ page: 1, search: searchQuery }, { replace: true });
        }, 500);

        return () => clearTimeout(delayDebounce);
    }, [searchQuery]);




    const handlePageChange = (page) => {
        fetchData(page, searchQuery);
        tableRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const hasAccess =
        userDetails?.admin?.role == 0 ||
        userDetails?.admin?.service_view == 1;

    if (!userDetails || !userDetails.admin) {
        return null;
    }

    if (!hasAccess) {
        return <AccessDenied />;
    }

    const handleDeleteClick = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#185A96",
            cancelButtonColor: "#ed2121",
            confirmButtonText: "Yes, delete it!",
            customClass: {
                popup: 'custom-swal-popup',
                title: 'custom-swal-title',
                content: 'custom-swal-content',
                confirmButton: 'custom-swal-button',
                cancelButton: 'custom-swal-button'
            }
        });
        if (result.isConfirmed) {
            if (id) {
                setLoading(true);
                try {
                    await apiInstance.delete(`/delete_service/${id}`);
                    Swal.fire("Deleted!", "Service has been deleted.", "success");
                    fetchData();
                } catch (error) {
                    Swal.fire(
                        "Error!",
                        error?.response?.data?.body || "Error deleting service",
                        "error"
                    );
                } finally {
                    setLoading(false);
                }
            }
        }
    };


    const canView = userDetails?.admin?.role == 0 || userDetails?.admin?.service_view == 1;
    const canUpdate = userDetails?.admin?.role == 0 || userDetails?.admin?.service_update == 1;
    const canAdd = userDetails?.admin?.role == 0 || userDetails?.admin?.service_add == 1;
    const canDelete = userDetails?.admin?.role == 0 || userDetails?.admin?.delete_service_permission == 1;
    const canSeeServiceTypes = userDetails?.admin?.role == 0 || userDetails?.admin?.service_type_view == 1;

    const options = ['S No.', 'Image', 'Name'];
    // if (canSeeServiceTypes) {
    //     options.push('Service Types');
    // }
    if (canUpdate || canView || canDelete) {
        options.push('Actions');
    }

    if (error) return <div>{error}<br />Please try again after some time or check your internet connection.</div>;

    return (
        <>
            <div className="container-fluid" id="tableContainer" ref={tableRef}>
                <div className="row">
                    <div className="col-12">
                        <div className="card my-4">
                            <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                                <div className="bg-info shadow-dark border-radius-lg d-flex justify-content-between align-items-center">
                                    <h6 className="text-white text-capitalize ps-3" id="tableHead">
                                        Services
                                    </h6>
                                </div>
                            </div>

                            <div className="section-body">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="align-items-center d-flex justify-content-end mb-3">
                                            <div className='me-1'>
                                                <input
                                                    type="text"
                                                    placeholder='Search'
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    className="border-1 form-control px-2"
                                                    style={{ maxWidth: '300px', backgroundColor: 'white' }}
                                                />
                                            </div>
                                            {canAdd && (
                                                <div>
                                                    <button
                                                        type="button"
                                                        data-tooltip-id="addBtn" data-tooltip-content="Add Service"
                                                        className="btn btn-info add_btn text-white my-0 mx-1"
                                                        onClick={() => navigate('/addService')}
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="table-responsive">
                                            <table className="table text-center align-items-center resturent_table">
                                                <thead>
                                                    <tr>
                                                        {options.map((option, indx) => (
                                                            <th key={indx}>{option}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>

                                                    {data &&
                                                        data.map((service, index) => (
                                                            <tr key={service.id}>
                                                                <td>{index + 1 + (currentPage - 1) * limit}</td>
                                                                <td>
                                                                    {service?.image ? (
                                                                        <img
                                                                            src={`${imageBaseUrl}/${service.image}`}
                                                                            alt="service"
                                                                            className='tableimg'
                                                                            style={{ objectFit: "cover" }}
                                                                        />
                                                                    ) : (
                                                                        <img
                                                                            src="/noimage.png"
                                                                            alt="service"
                                                                            className='tableimg'
                                                                            style={{ objectFit: "cover" }}
                                                                        />
                                                                    )}
                                                                </td>
                                                                <td>{service?.name || ''}</td>
                                                                {/* {canSeeServiceTypes && (
                                                                    <td>
                                                                        <Link
                                                                            data-tooltip-id="my-tooltip" data-tooltip-content="Service Types"
                                                                            to={`/serviceTypes/${service.id}`}
                                                                            className="has-icon btn m-1 bg-info"
                                                                            style={{
                                                                                color: 'white',
                                                                            }}
                                                                        >
                                                                            <i className="me-100 fas fa-eye" />
                                                                        </Link>
                                                                    </td>
                                                                )} */}
                                                                {(canView || canUpdate || canDelete) && (
                                                                    <td>
                                                                        {canView && (
                                                                            <Link
                                                                                data-tooltip-id="my-tooltip" data-tooltip-content="View"
                                                                                to={`/viewService/${service.id}?page=${currentPage}&search=${searchQuery}`}
                                                                                className="has-icon btn m-1 bg-info"
                                                                                style={{
                                                                                    color: 'white',
                                                                                }}
                                                                            >
                                                                                <i className="me-100 fas fa-eye" />
                                                                            </Link>
                                                                        )}
                                                                        {canUpdate && (
                                                                            <Link
                                                                                data-tooltip-id="my-tooltip" data-tooltip-content="Edit"
                                                                                to={`/editService/${service.id}?page=${currentPage}&search=${searchQuery}`}
                                                                                className="has-icon btn m-1 bg-info"
                                                                                style={{
                                                                                    color: 'white',
                                                                                }}
                                                                            >
                                                                                <i className="me-100 fas fa-edit" />
                                                                            </Link>
                                                                        )}
                                                                        {canDelete && (
                                                                            <button
                                                                                onClick={() => handleDeleteClick(service?.id)}
                                                                                className="has-icon btn m-1 bg-danger"
                                                                                data-tooltip-id="my-tooltip" data-tooltip-content="Delete"
                                                                                style={{
                                                                                    borderColor: '#110011',
                                                                                    color: '#fff',
                                                                                }}
                                                                            >
                                                                                <i className="me-100 fas fa-trash" />
                                                                            </button>
                                                                        )}
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        {loading ? <div></div> : data?.length === 0 && <div className='text-info text-center' style={{ fontSize: '20px' }}>No data found..</div>}

                                        {totalPages > 1 &&
                                            <div className="row">
                                                <nav aria-label="Page navigation example" className="d-flex justify-content-end w-100" style={{ background: "none" }}>
                                                    <ul className="pagination">
                                                        {/* Previous Button */}
                                                        {currentPage > 1 && (
                                                            <li className="page-item">
                                                                <button
                                                                    className="page-link orange_last"
                                                                    style={{ color: "#185A96" }}
                                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                                >
                                                                    <i className="fas fa-chevron-left"></i>
                                                                </button>
                                                            </li>
                                                        )}

                                                        {(() => {
                                                            const pageNumbers = [];

                                                            if (totalPages <= 4) {
                                                                // Show all pages if totalPages <= 4
                                                                for (let i = 1; i <= totalPages; i++) {
                                                                    pageNumbers.push(i);
                                                                }
                                                            } else {
                                                                if (currentPage <= 2) {
                                                                    pageNumbers.push(1, 2, '...', totalPages);
                                                                } else if (currentPage >= totalPages - 1) {
                                                                    pageNumbers.push(1, '...', totalPages - 1, totalPages);
                                                                } else {
                                                                    pageNumbers.push(1, '...', currentPage, totalPages);
                                                                }
                                                            }

                                                            return pageNumbers.map((page, idx) => {
                                                                const isActive = page === currentPage;
                                                                return (
                                                                    <li key={idx} className={`page-item ${isActive ? 'active' : ''}`}>
                                                                        {page === '...' ? (
                                                                            <span className="page-link" style={{ color: "#185A96" }}>...</span>
                                                                        ) : (
                                                                            <button
                                                                                className="page-link"
                                                                                style={{
                                                                                    color: isActive ? "white" : "#185A96",
                                                                                    backgroundColor: isActive ? "#185A96" : "transparent",
                                                                                    borderColor: isActive ? "#185A96" : "#dee2e6"
                                                                                }}
                                                                                onClick={() => handlePageChange(page)}
                                                                            >
                                                                                {page}
                                                                            </button>
                                                                        )}
                                                                    </li>
                                                                );
                                                            });

                                                        })()}

                                                        {/* Next Button */}
                                                        {currentPage < totalPages && (
                                                            <li className="page-item">
                                                                <button
                                                                    className="page-link orange_last"
                                                                    style={{ color: "#185A96" }}
                                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                                >
                                                                    <i className="fas fa-chevron-right"></i>
                                                                </button>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </nav>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {loading && (
                <div className="loader" style={{ position: 'absolute', top: '50%', left: '59%', transform: 'translate(-50%, -50%)' }}>
                    <div className="spinner-border text-info" role="status" style={{ width: '50px', height: '50px' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}
            <Tooltip id="my-tooltip" place="top" type="dark" effect="solid" />
            <Tooltip id="addBtn" place="bottom" type="dark" effect="solid" style={{ zIndex: '100' }} />
        </>
    );
};

export default CategoryList;